/**
 * One dependency graph over every read in a workspace (F3 and F19 of
 * docs/plans/field-relations.md).
 *
 * Every read is an edge from the field read to the field that reads it: an own
 * field read, a `hop`, an `agg` over lines, a rollup's `via`, `of` and `where`,
 * a default (Starts with), a Show when, an Editable when, a Must be, and a
 * Link's mapping. The graph must hold no ring, and a ring is refused when a
 * field is saved, by a refusal that names every field in it.
 *
 * Two kinds of edge are held but never close a ring:
 *
 *  - A default's `hop`. A Starts with fills once, when a form opens, from the
 *    other row as it is stored; nothing moves it again when that row moves.
 *    So the renewal's Start date may read the member's Current expiry, the max
 *    of End date, while End date starts with Start date (F22).
 *  - A rule that writes nothing: an Editable when and a Must be (F13). Their
 *    edges land on a node of their own that nothing reads.
 *
 * The shapes read are the ones the structure planner and the spec check read:
 * a field's raw `spec` (`readFieldSpec`), its Starts with and Show when as
 * recipes, and a Link's field map. Ids are whatever the caller keys by: field
 * and record ids on the server, keys in a setup spec.
 */
import {
  formulaFieldsRead,
  formulaHopsRead,
  formulaLinesRead,
  formulaOf,
  isComputedType,
  linesOf,
  readFieldSpec,
  rollupOf,
  type FormulaProblem,
  type FormulaSpec,
} from './formula';
import type { LinkFieldMap } from './links';
import type { Trigger } from './rules/trigger';

/** One field, as the graph reads it. */
export type GraphField = {
  readonly id: string;
  readonly label: string;
  readonly type: string;
  /** The record a reference or a multi_select points at. */
  readonly targetRecordId: string | null;
  /** `app.field.spec`, raw: a formula, a rollup, a lines field's record. */
  readonly spec?: unknown;
  readonly startsWith?: FormulaSpec | null;
  /**
   * True when `startsWith` fills once (F11): it never follows its inputs, so
   * its read of a worked-out field is held but never closes a ring.
   */
  readonly startsOnce?: boolean;
  readonly showWhen?: FormulaSpec | null;
  readonly editableWhen?: FormulaSpec | null;
  readonly mustBe?: readonly FormulaSpec[] | null;
  /** A `lines` field's lines record, when the caller names it outside `spec` (a setup spec does). */
  readonly linesRecordId?: string | null;
};

/**
 * One record and its live fields, and the Must be rules on the whole entry
 * (`app.record.prefs.rules`, F13): each lands on a rule node that writes
 * nothing, so it never closes a ring but is a dependent of every field it reads.
 */
export type GraphRecord = {
  readonly id: string;
  readonly name?: string;
  readonly fields: readonly GraphField[];
  readonly rules?: readonly FormulaSpec[] | null;
};

/** One Link that writes rows: what it reads on `fromRecordId` and what it fills on `toRecordId`. */
export type GraphLink = {
  readonly fromRecordId: string;
  readonly toRecordId: string;
  readonly fieldMap: LinkFieldMap;
  readonly trigger?: Trigger;
};

export type GraphEdgeKind =
  | 'field'
  | 'hop'
  | 'agg'
  | 'rollup'
  | 'default'
  | 'show_when'
  | 'editable_when'
  | 'must_be'
  | 'link';

/** A node: `recordId|fieldId`, or `recordId|fieldId|rule` for a rule that writes nothing. */
export type FieldNode = string;

export type GraphEdge = {
  /** The field read. */
  readonly from: FieldNode;
  /** The field (or rule) that reads it. */
  readonly to: FieldNode;
  readonly kind: GraphEdgeKind;
  /** Held, but never part of a ring (a default's hop, F22). */
  readonly stored: boolean;
};

export type FieldGraph = {
  readonly edges: readonly GraphEdge[];
  /** Every edge out of a node, to the nodes that read it. */
  readonly readBy: ReadonlyMap<FieldNode, readonly GraphEdge[]>;
  /** The words for a node: its field's label. */
  readonly labelOf: (node: FieldNode) => string;
  /** The type of a node's field, or null for a rule node or a field not in the graph. */
  readonly typeOf: (node: FieldNode) => string | null;
};

export const nodeOf = (recordId: string, fieldId: string): FieldNode => `${recordId}|${fieldId}`;

const ruleNode = (recordId: string, fieldId: string, rule: string): FieldNode => `${recordId}|${fieldId}|${rule}`;

/** The field part of a record rule's node: a rule on the whole entry sits on no field. */
export const RECORD_RULES = '*';

/**
 * The graph over these records and Links. A read that names nothing (a field
 * gone, a hop through a field that is not a reference) is left out: the
 * checks that name what is missing are the formula checker's, not this one's.
 */
export const fieldGraph = (records: readonly GraphRecord[], links: readonly GraphLink[] = []): FieldGraph => {
  const byRecord = new Map(records.map((record) => [record.id, new Map(record.fields.map((field) => [field.id, field]))]));
  const edges: GraphEdge[] = [];
  const has = (recordId: string, fieldId: string): boolean => byRecord.get(recordId)?.has(fieldId) === true;
  const add = (from: [string, string], to: FieldNode, kind: GraphEdgeKind, stored = false, computedStored = false): void => {
    if (!has(from[0], from[1])) return;
    const worked = computedStored && isComputedType(byRecord.get(from[0])?.get(from[1])?.type ?? '');
    edges.push({ from: nodeOf(from[0], from[1]), to, kind, stored: stored || worked });
  };

  /** Where a field a reference or a lines field holds rows of lives. */
  const rowsOf = (recordId: string, fieldId: string): string | null => {
    const field = byRecord.get(recordId)?.get(fieldId);
    if (field === undefined) return null;
    if (field.type === 'lines') return field.linesRecordId ?? linesOf(readFieldSpec(field.spec))?.recordId ?? null;
    return field.targetRecordId;
  };

  /** Every read of one recipe on `recordId`, into `to`. */
  const recipe = (
    recordId: string,
    spec: FormulaSpec,
    to: FieldNode,
    kind: GraphEdgeKind,
    hopsStored = false,
    computedStored = false,
  ): void => {
    const hopped = new Set(formulaHopsRead(spec).map((hop) => hop.via));
    const summed = new Set(formulaLinesRead(spec).map((one) => one.lines));
    for (const fieldId of formulaFieldsRead(spec)) {
      // The `via` of a hop and the lines of an agg are read too: a new pick or
      // a new line moves the answer.
      add([recordId, fieldId], to, kind === 'field' && hopped.has(fieldId) ? 'hop' : kind === 'field' && summed.has(fieldId) ? 'agg' : kind, false, computedStored);
    }
    for (const hop of formulaHopsRead(spec)) {
      const there = rowsOf(recordId, hop.via);
      if (there !== null) add([there, hop.fieldId], to, kind === 'field' ? 'hop' : kind, hopsStored, computedStored);
    }
    for (const one of formulaLinesRead(spec)) {
      const there = rowsOf(recordId, one.lines);
      if (there === null) continue;
      for (const fieldId of one.fields) add([there, fieldId], to, kind === 'field' ? 'agg' : kind, false, computedStored);
    }
  };

  for (const record of records) {
    for (const field of record.fields) {
      const self = nodeOf(record.id, field.id);
      const held = readFieldSpec(field.spec);
      const formula = field.type === 'formula' ? formulaOf(held) : null;
      if (formula !== null) recipe(record.id, formula, self, 'field');
      const rollup = field.type === 'rollup' ? rollupOf(held) : null;
      if (rollup !== null) {
        for (const fieldId of [rollup.via, ...(rollup.of === undefined ? [] : [rollup.of]), ...(rollup.where ?? []).map((one) => one.field)]) {
          add([rollup.over, fieldId], self, 'rollup');
        }
      }
      // A fill-once default may read a worked-out field (F11): it never
      // follows it, so that read never closes a ring.
      if (field.startsWith) recipe(record.id, field.startsWith, self, 'default', true, field.startsOnce === true);
      if (field.showWhen) recipe(record.id, field.showWhen, self, 'show_when');
      if (field.editableWhen) recipe(record.id, field.editableWhen, ruleNode(record.id, field.id, 'editable'), 'editable_when');
      (field.mustBe ?? []).forEach((rule, index) => recipe(record.id, rule, ruleNode(record.id, field.id, `must${index}`), 'must_be'));
    }
    (record.rules ?? []).forEach((rule, index) => recipe(record.id, rule, ruleNode(record.id, RECORD_RULES, `must${index}`), 'must_be'));
  }

  for (const link of links) {
    const triggerReads = (link.trigger?.all ?? []).flatMap((group) => group.any);
    for (const [targetId, mapping] of Object.entries(link.fieldMap)) {
      if (!has(link.toRecordId, targetId)) continue;
      const to = nodeOf(link.toRecordId, targetId);
      if (mapping.kind === 'field') add([link.fromRecordId, mapping.fieldId], to, 'link');
      if (mapping.kind === 'reference') {
        add([link.fromRecordId, mapping.via], to, 'link');
        const there = rowsOf(link.fromRecordId, mapping.via);
        if (there !== null) add([there, mapping.fieldId], to, 'link');
      }
      if (mapping.kind === 'formula') recipe(link.fromRecordId, mapping.spec, to, 'link');
      for (const condition of triggerReads) {
        if (condition.via === undefined) add([link.fromRecordId, condition.fieldId], to, 'link');
        else {
          add([link.fromRecordId, condition.via], to, 'link');
          const there = rowsOf(link.fromRecordId, condition.via);
          if (there !== null) add([there, condition.fieldId], to, 'link');
        }
      }
    }
  }

  const readBy = new Map<FieldNode, GraphEdge[]>();
  for (const edge of edges) readBy.set(edge.from, [...(readBy.get(edge.from) ?? []), edge]);
  const labelOf = (node: FieldNode): string => {
    const [recordId = '', fieldId = ''] = node.split('|');
    return byRecord.get(recordId)?.get(fieldId)?.label ?? fieldId;
  };
  const typeOf = (node: FieldNode): string | null => {
    const [recordId = '', fieldId = '', rule] = node.split('|');
    return rule === undefined ? (byRecord.get(recordId)?.get(fieldId)?.type ?? null) : null;
  };
  return { edges, readBy, labelOf, typeOf };
};

/**
 * Every ring in the graph, each as its nodes in reading order (the first node
 * is read by the second, and the last reads... the first again). A field that
 * reads itself is a ring of one. Edges marked `stored` never close one.
 */
export const ringsOf = (graph: FieldGraph): readonly (readonly FieldNode[])[] => {
  // Tarjan's strongly connected components over the edges that can close a ring.
  const live = (node: FieldNode): readonly GraphEdge[] => (graph.readBy.get(node) ?? []).filter((edge) => !edge.stored);
  const nodes = [...new Set(graph.edges.flatMap((edge) => [edge.from, edge.to]))];
  const index = new Map<FieldNode, number>();
  const low = new Map<FieldNode, number>();
  const onStack = new Set<FieldNode>();
  const stack: FieldNode[] = [];
  const out: FieldNode[][] = [];
  let next = 0;
  const visit = (node: FieldNode): void => {
    index.set(node, next);
    low.set(node, next);
    next += 1;
    stack.push(node);
    onStack.add(node);
    for (const edge of live(node)) {
      if (!index.has(edge.to)) {
        visit(edge.to);
        low.set(node, Math.min(low.get(node)!, low.get(edge.to)!));
      } else if (onStack.has(edge.to)) {
        low.set(node, Math.min(low.get(node)!, index.get(edge.to)!));
      }
    }
    if (low.get(node) !== index.get(node)) return;
    const component: FieldNode[] = [];
    for (;;) {
      const top = stack.pop()!;
      onStack.delete(top);
      component.push(top);
      if (top === node) break;
    }
    const selfRead = component.length === 1 && live(node).some((edge) => edge.to === node);
    if (component.length > 1 || selfRead) out.push(orderRing(component, live));
  };
  for (const node of nodes) if (!index.has(node)) visit(node);
  return out;
};

/** A component laid out in reading order, as far as one walk around it goes. */
const orderRing = (component: readonly FieldNode[], live: (node: FieldNode) => readonly GraphEdge[]): FieldNode[] => {
  const inside = new Set(component);
  const start = [...component].sort()[0]!;
  const ordered = [start];
  const seen = new Set([start]);
  for (let at = start; ; ) {
    const step = live(at).find((edge) => inside.has(edge.to) && !seen.has(edge.to));
    if (step === undefined) break;
    ordered.push(step.to);
    seen.add(step.to);
    at = step.to;
  }
  for (const node of component) if (!seen.has(node)) ordered.push(node);
  return ordered;
};

/** Every field that reads this one, directly or through another, in no set order. */
export const dependentsOf = (graph: FieldGraph, node: FieldNode): readonly FieldNode[] => {
  const seen = new Set<FieldNode>();
  const queue = [node];
  while (queue.length > 0) {
    const at = queue.pop()!;
    for (const edge of graph.readBy.get(at) ?? []) {
      if (seen.has(edge.to) || edge.to === node) continue;
      seen.add(edge.to);
      queue.push(edge.to);
    }
  }
  return [...seen];
};

/** The kinds of edge that run around one ring. */
export const ringKinds = (graph: FieldGraph, ring: readonly FieldNode[]): ReadonlySet<GraphEdgeKind> => {
  const inside = new Set(ring);
  return new Set(graph.edges.filter((edge) => !edge.stored && inside.has(edge.from) && inside.has(edge.to)).map((edge) => edge.kind));
};

/**
 * The refusal for one ring, naming every field in it, said from `from` when it
 * is in the ring: `"Amount" reads "Bonus", which reads "Amount", and that is a
 * circle.` A ring that runs up the tree of lines (a line reads a field above
 * it that totals the lines) keeps its own message, FORMULA_PARENT_COMPUTED
 * (F19). The problem names the field the first one reads.
 */
export const ringProblem = (graph: FieldGraph, ring: readonly FieldNode[], from?: FieldNode): FormulaProblem => {
  // A ring runs from the field read to the one that reads it; a sentence says
  // who reads whom, so it walks the other way.
  const readers = [...ring].reverse();
  const at = from === undefined ? -1 : readers.indexOf(from);
  const turned = at <= 0 ? readers : [...readers.slice(at), ...readers.slice(0, at)];
  const names = turned.map((node) => `"${graph.labelOf(node)}"`);
  const fieldId = (turned[1] ?? turned[0] ?? '').split('|')[1];
  const named = fieldId === undefined ? {} : { fieldId };
  const kinds = ringKinds(graph, ring);
  if (kinds.has('agg') && kinds.has('hop')) {
    return {
      code: 'FORMULA_PARENT_COMPUTED',
      message: `${names.join(', ')} wait on each other: a line reads a field above it that totals the lines, and that is a circle.`,
      ...named,
    };
  }
  const message =
    names.length === 1
      ? `${names[0]} reads itself, and that is a circle.`
      : `${names[0]} reads ${[...names.slice(1), names[0]].join(', which reads ')}, and that is a circle.`;
  return { code: 'FORMULA_CYCLE', message, ...named };
};

/** The rings that run through any of these nodes. */
export const ringsThrough = (graph: FieldGraph, nodes: Iterable<FieldNode>): readonly (readonly FieldNode[])[] => {
  const wanted = new Set(nodes);
  return ringsOf(graph).filter((ring) => ring.some((node) => wanted.has(node) || wanted.has(node.split('|').slice(0, 2).join('|'))));
};

/**
 * The records with one field put in place (added, or with its recipe
 * replaced): the graph a save would leave. The field keeps every other part of
 * what it held.
 */
export const withField = (
  records: readonly GraphRecord[],
  recordId: string,
  field: Partial<GraphField> & Pick<GraphField, 'id'>,
): readonly GraphRecord[] => {
  const found = records.some((record) => record.id === recordId);
  const placed = (fields: readonly GraphField[]): readonly GraphField[] => {
    const held = fields.find((one) => one.id === field.id);
    const merged: GraphField = {
      label: field.id,
      type: 'text',
      targetRecordId: null,
      ...held,
      ...field,
    };
    return held === undefined ? [...fields, merged] : fields.map((one) => (one.id === field.id ? merged : one));
  };
  return found
    ? records.map((record) => (record.id === recordId ? { ...record, fields: placed(record.fields) } : record))
    : [...records, { id: recordId, fields: placed([]) }];
};

/** Every node this one reads, directly or through another, over the edges that can close a ring. */
export const readsOf = (graph: FieldGraph, node: FieldNode): readonly FieldNode[] => {
  const reads = new Map<FieldNode, FieldNode[]>();
  for (const edge of graph.edges) if (!edge.stored) reads.set(edge.to, [...(reads.get(edge.to) ?? []), edge.from]);
  const seen = new Set<FieldNode>();
  const queue = [node];
  while (queue.length > 0) {
    const at = queue.pop()!;
    for (const from of reads.get(at) ?? []) {
      if (seen.has(from) || from === node) continue;
      seen.add(from);
      queue.push(from);
    }
  }
  return [...seen];
};

/**
 * What the graph refuses of one recipe on `recordId`'s `fieldId`: every ring
 * the save would close through it, each named in full. For a formula's
 * `graphProblems` (formula.ts).
 */
export const formulaGraphProblems = (
  records: readonly GraphRecord[],
  recordId: string,
  field: Partial<GraphField> & Pick<GraphField, 'id'>,
  links: readonly GraphLink[] = [],
): readonly FormulaProblem[] => {
  const graph = fieldGraph(withField(records, recordId, field), links);
  const self = nodeOf(recordId, field.id);
  // A recipe that reads its own field is named by the checker already (`typeOf`).
  return ringsThrough(graph, [self])
    .filter((ring) => !(ring.length === 1 && ring[0] === self))
    .map((ring) => ringProblem(graph, ring, self));
};

/**
 * What the graph refuses of one rollup on `recordId`'s `fieldId` (F19): a ring
 * through it (its amount reads, a hop away, the total it feeds), or an amount
 * that waits on another total. The write moves one level of totals: an entry's
 * write moves the totals that count it, and a total's own version moves no
 * other total, so a total of a total would drift.
 */
export const rollupGraphProblems = (
  records: readonly GraphRecord[],
  recordId: string,
  field: Partial<GraphField> & Pick<GraphField, 'id'>,
  links: readonly GraphLink[] = [],
): readonly FormulaProblem[] => {
  const graph = fieldGraph(withField(records, recordId, { type: 'rollup', ...field }), links);
  const self = nodeOf(recordId, field.id);
  const rings = ringsThrough(graph, [self]);
  if (rings.length > 0) return rings.map((ring) => ringProblem(graph, ring, self));
  const rollup = rollupOf(readFieldSpec(field.spec));
  if (rollup?.of === undefined) return [];
  const of = nodeOf(rollup.over, rollup.of);
  const under = readsOf(graph, of).find((node) => graph.typeOf(node) === 'rollup');
  if (under === undefined) return [];
  return [
    {
      code: 'ROLLUP_OF',
      message: `"${graph.labelOf(of)}" waits on the total "${graph.labelOf(under)}", so it cannot be added up into another.`,
      fieldId: rollup.of,
    },
  ];
};

/**
 * Every total in these records whose amount waits on another total, where the
 * wait runs through one of `touched`: the save that made it one level too
 * deep. This is the patch hole of F19: a formula that is some total's amount,
 * changed to read a total, is refused like a total added over it.
 */
export const deepTotalsThrough = (
  graph: FieldGraph,
  records: readonly GraphRecord[],
  touched: Iterable<FieldNode>,
): readonly FormulaProblem[] => {
  const wanted = new Set(touched);
  const out: FormulaProblem[] = [];
  for (const record of records) {
    for (const field of record.fields) {
      if (field.type !== 'rollup') continue;
      const rollup = rollupOf(readFieldSpec(field.spec));
      if (rollup?.of === undefined) continue;
      const of = nodeOf(rollup.over, rollup.of);
      const reads = readsOf(graph, of);
      const under = reads.find((node) => graph.typeOf(node) === 'rollup');
      if (under === undefined) continue;
      if (!wanted.has(nodeOf(record.id, field.id)) && !wanted.has(of) && !reads.some((node) => wanted.has(node))) continue;
      out.push({
        code: 'ROLLUP_OF',
        message: `"${graph.labelOf(of)}" waits on the total "${graph.labelOf(under)}", so the total "${field.label}" cannot add it up.`,
        fieldId: rollup.of,
      });
    }
  }
  return out;
};
